import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  // The reference's signature control: solid lime, near-black ink.
  primary:
    'bg-accent text-accent-ink hover:bg-accent-hover active:bg-accent-active font-medium ' +
    'disabled:bg-accent/40 disabled:text-accent-ink/60',
  secondary:
    'bg-transparent text-ink border border-hairline-strong hover:bg-surface-raised ' +
    'active:bg-surface disabled:text-faint',
  ghost: 'bg-transparent text-muted hover:text-ink hover:bg-surface-raised disabled:text-faint',
  danger:
    'bg-transparent text-danger border border-danger/40 hover:bg-danger-soft ' +
    'active:bg-danger-soft disabled:text-danger/40',
};

const SIZES: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm gap-2 rounded-sm',
  md: 'h-11 px-4 text-[0.9375rem] gap-2 rounded-md',
  // Matches the tall primary CTA in the reference.
  lg: 'h-14 sm:h-16 px-5 sm:px-6 text-base gap-3 rounded-md',
};

interface CommonProps {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** Rendered flush right - the reference pushes the arrow to the far edge. */
  trailingIcon?: ReactNode;
  leadingIcon?: ReactNode;
  fullWidth?: boolean;
  children?: ReactNode;
  className?: string;
}

const BASE =
  'relative inline-flex items-center justify-center whitespace-nowrap ' +
  'transition-colors duration-fast ease-out ' +
  'disabled:cursor-not-allowed select-none';

function inner(
  { children, leadingIcon, trailingIcon, loading }: CommonProps,
  spread: boolean,
): ReactNode {
  return (
    <>
      {loading ? <Spinner className="size-4 shrink-0" /> : leadingIcon}
      <span className={cn(spread && 'flex-1 text-left', 'truncate')}>{children}</span>
      {trailingIcon}
    </>
  );
}

export type ButtonProps = CommonProps & ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    trailingIcon,
    leadingIcon,
    fullWidth = false,
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  // With both a label and a trailing icon we justify apart, as in the mockup.
  const spread = Boolean(trailingIcon) && size === 'lg';
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        BASE,
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        spread && 'justify-between',
        className,
      )}
      {...rest}
    >
      {inner({ children, leadingIcon, trailingIcon, loading }, spread)}
    </button>
  );
});

export type ButtonLinkProps = CommonProps & {
  to: string;
  replace?: boolean;
  state?: unknown;
  'aria-label'?: string;
};

export function ButtonLink({
  variant = 'primary',
  size = 'md',
  trailingIcon,
  leadingIcon,
  fullWidth = false,
  className,
  children,
  to,
  ...rest
}: ButtonLinkProps) {
  const spread = Boolean(trailingIcon) && size === 'lg';
  return (
    <Link
      to={to}
      className={cn(
        BASE,
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        spread && 'justify-between',
        className,
      )}
      {...rest}
    >
      {inner({ children, leadingIcon, trailingIcon }, spread)}
    </Link>
  );
}
